import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, Download, Eye, RefreshCw, ShieldAlert, X } from 'lucide-react';
import type { DiffSegment, DocEditRecoveryResult, DocumentVersionSummary, Edit, EditApplyOutcome, EditBatch, HtmlEditApplyResult } from '@corner/shared';
import { downloadUrlWithAuthIfNeeded } from '../../../lib/authedDownload';
import { APP } from '../tokens';

const F = APP.fontFamily;

const PDF_ELIGIBILITY_REASON_TEXT: Record<string, string> = {
  anchor_not_found: 'Corner could not find a stable anchor for this text in the original PDF.',
  ambiguous_anchor: 'This text appears in more than one matching place in the PDF.',
  cross_line: 'This change spans multiple lines, which could shift the PDF layout.',
  cross_block: 'This change crosses separate text blocks in the PDF.',
  bbox_overflow: 'The replacement does not fit inside the original text box.',
  layout_shift_risk: 'This change may reflow the PDF layout.',
  unverified_replacement: 'Corner could not verify this replacement against the original PDF.',
  verification_unavailable: 'Layout-preserving verification is temporarily unavailable for this file.',
  source_pdf_missing: 'Corner could not load the original PDF needed to verify this edit.',
  infra_unavailable: 'Layout-preserving verification is temporarily unavailable. Please retry.',
  ok: 'This change can preserve the original PDF layout.',
};

/**
 * UI states the inline review card can be in. Driven by the
 * authoritative `EditApplyOutcome` returned by the backend, with `idle`
 * and `applying` as transient client-only states.
 *
 * Why a state machine: previous code inferred state from a mix of
 * `busyKey`, `errorMessage`, `data.code`, and `resultingVersion.fileId`,
 * which left infra failures looking the same as unsafe edits and
 * blocked the user from retrying transient errors.
 */
type CardStateKind =
  | 'idle'
  | 'applying'
  | 'applied'
  | 'partial'
  | 'blocked_unsafe'
  | 'blocked_infra'
  | 'verification_unavailable'
  | 'stale'
  | 'error';

interface CardState {
  kind: CardStateKind;
  outcome: EditApplyOutcome | null;
  detail?: string;
}

function pdfEligibilityForEdit(edit: Edit) {
  if (edit.pdfOverlayEligibility) return edit.pdfOverlayEligibility;
  if (edit.verification?.status === 'verified') {
    return {
      overlayEligible: true,
      reason: 'ok' as const,
      page: edit.anchor?.page ?? edit.location.page,
      anchorId: edit.anchor?.pdfTextRunId ?? edit.anchor?.blockAnchorId ?? edit.location.anchor,
      bbox: edit.anchor?.bbox,
      tier: edit.verification.tier,
    };
  }
  if (edit.verification?.status === 'failed') {
    return {
      overlayEligible: false,
      reason: edit.verification.reason ?? 'unverified_replacement' as const,
      page: edit.anchor?.page ?? edit.location.page,
      anchorId: edit.anchor?.pdfTextRunId ?? edit.anchor?.blockAnchorId ?? edit.location.anchor,
      bbox: edit.anchor?.bbox,
      tier: edit.verification.tier,
    };
  }
  return undefined;
}

function blockedDetailsFromBatch(batch: EditBatch) {
  return batch.edits
    .filter((edit) => pdfEligibilityForEdit(edit)?.overlayEligible === false)
    .map((edit) => {
      const eligibility = pdfEligibilityForEdit(edit);
      return {
        editId: edit.id,
        reason: eligibility?.reason ?? 'unverified_replacement' as const,
        page: eligibility?.page ?? edit.location.page ?? null,
      };
    });
}

function blockedReasonsForBatch(batch: EditBatch): string[] {
  return Array.from(new Set(blockedDetailsFromBatch(batch).map((detail) => detail.reason)));
}

function outcomeFromBatch(batch: EditBatch): EditApplyOutcome | null {
  if (batch.lastOutcome) return batch.lastOutcome;
  const appliedCount = batch.edits.filter((edit) => edit.status === 'accepted').length;
  const fileId = batch.resultingVersion?.fileId ?? undefined;
  const versionId = batch.resultingVersionId ?? null;
  const projectId = batch.resultingProject?.id ?? null;
  const blocked = blockedDetailsFromBatch(batch);
  switch (batch.status) {
    case 'applied':
      return fileId
        ? {
            kind: batch.edits.some((edit) => edit.status === 'accepted' && pdfEligibilityForEdit(edit)?.tier === 'local_reflow')
              ? 'applied_local_reflow'
              : 'applied_exact',
            batchId: batch.id,
            appliedCount,
            fileId,
            versionId,
            projectId,
          }
        : null;
    case 'partial':
      return fileId ? { kind: 'partial', batchId: batch.id, appliedCount, blocked, fileId, versionId, projectId } : null;
    case 'blocked_infra':
      return {
        kind: 'blocked_infra',
        batchId: batch.id,
        detail: batch.applyFailure?.reason || 'Layout-preserving verification is temporarily unavailable for this file.',
        canRetry: true,
        blocked,
      };
    case 'blocked_unsafe':
      if (batch.editPolicy === 'signed_final_pdf' && batch.applyFailure?.code === 'PDF_FINAL_DOCUMENT_EXACT_OVERLAY_REQUIRED') {
        return {
          kind: 'blocked_final_document',
          batchId: batch.id,
          blocked,
          detail: batch.applyFailure?.reason,
        };
      }
      if (batch.editPolicy === 'signed_final_pdf' && batch.applyFailure?.code === 'PDF_BBOX_OVERFLOW') {
        return {
          kind: 'blocked_overflow',
          batchId: batch.id,
          blocked,
          detail: batch.applyFailure?.reason,
        };
      }
      return {
        kind: 'blocked_unsafe',
        batchId: batch.id,
        blocked,
        detail: batch.applyFailure?.reason,
      };
    case 'verification_unavailable':
      return {
        kind: 'blocked_unsafe',
        batchId: batch.id,
        blocked,
        detail: batch.applyFailure?.reason || 'Layout-preserving verification is unavailable for this PDF.',
      };
    case 'error':
      return {
        kind: 'error',
        batchId: batch.id,
        detail: batch.applyFailure?.reason || 'Corner could not apply these changes.',
        code: batch.applyFailure?.code,
      };
    default:
      return null;
  }
}

function deriveStateFromBatch(batch: EditBatch, busyKey: string | null): CardState {
  if (busyKey === 'accept-all' || busyKey === 'apply-html') {
    return { kind: 'applying', outcome: null };
  }
  const outcome = outcomeFromBatch(batch);
  if (outcome) return stateFromOutcome(outcome);
  return { kind: 'idle', outcome: null };
}

function stateFromOutcome(outcome: EditApplyOutcome): CardState {
  switch (outcome.kind) {
    case 'applied':
    case 'applied_exact':
    case 'applied_local_reflow':
      return { kind: 'applied', outcome };
    case 'partial':
      return { kind: 'partial', outcome };
    case 'blocked_unsafe':
    case 'blocked_overflow':
    case 'blocked_final_document':
      return { kind: 'blocked_unsafe', outcome, detail: outcome.detail };
    case 'blocked_infra':
      return { kind: 'blocked_infra', outcome, detail: outcome.detail };
    case 'verification_unavailable':
      return { kind: 'blocked_unsafe', outcome, detail: outcome.detail };
    case 'error':
      return { kind: 'error', outcome, detail: outcome.detail };
  }
}

function isBlockedApplyState(state: CardState): boolean {
  return (
    state.kind === 'blocked_unsafe' ||
    state.kind === 'blocked_infra' ||
    state.kind === 'verification_unavailable' ||
    state.kind === 'stale' ||
    state.kind === 'error'
  );
}

function overlayEligibleCount(batch: EditBatch): number {
  return batch.edits.filter((edit) => edit.status === 'pending' && pdfEligibilityForEdit(edit)?.overlayEligible === true).length;
}

function isEditableCopyFromSignedPdf(version?: DocumentVersionSummary | null): boolean {
  return Boolean(
    version &&
      version.source === 'conversion' &&
      version.lineageKind === 'copy' &&
      version.changeSummary === 'Editable copy from signed PDF',
  );
}

function isSignedFinalVersion(version?: DocumentVersionSummary | null): boolean {
  if (!version) return false;
  if (isEditableCopyFromSignedPdf(version)) return false;
  if (version.source === 'signed') return true;
  const name = `${version.fileName ?? ''} ${version.changeSummary ?? ''}`.toLowerCase();
  return version.mimeType === 'application/pdf' && /\b(signed|final|executed)\b/.test(name);
}

function isStructuredSourceVersion(version?: DocumentVersionSummary | null): boolean {
  if (!version || isSignedFinalVersion(version)) return false;
  return (
    version.mimeType === 'text/html' ||
    version.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    /\.(docx?|html?)$/i.test(version.fileName ?? '')
  );
}

function findUnsignedVersionTarget(batch: EditBatch, versions: DocumentVersionSummary[]): DocumentVersionSummary | null {
  const unsigned = versions.filter((version) => !isSignedFinalVersion(version));
  if (unsigned.length === 0) return null;
  const signedVersion =
    versions.find((version) => version.id === batch.baseVersionId) ??
    versions.find((version) => version.fileId === batch.docId) ??
    null;
  return (
    unsigned.find((version) => version.id === batch.baseVersionId) ??
    (signedVersion?.createdFromVersionId
      ? unsigned.find((version) => version.id === signedVersion.createdFromVersionId)
      : null) ??
    unsigned[unsigned.length - 1] ??
    null
  );
}

function findSourceDocumentTarget(batch: EditBatch, versions: DocumentVersionSummary[]): DocumentVersionSummary | null {
  const structured = versions.filter(isStructuredSourceVersion);
  if (structured.length === 0) return null;
  const unsignedTarget = findUnsignedVersionTarget(batch, versions);
  if (unsignedTarget && isStructuredSourceVersion(unsignedTarget)) return unsignedTarget;
  return structured[structured.length - 1] ?? null;
}

function isFinalDocumentPolicyBlock(batch: EditBatch, state: CardState): boolean {
  if (batch.editPolicy !== 'signed_final_pdf') return false;
  if (
    state.kind === 'blocked_unsafe' &&
    (state.outcome?.kind === 'blocked_final_document' || state.outcome?.kind === 'blocked_overflow')
  ) {
    return true;
  }
  return batch.edits.some((edit) => (
    edit.status === 'pending' &&
    pdfEligibilityForEdit(edit)?.overlayEligible === false &&
    pdfEligibilityForEdit(edit)?.reason === 'bbox_overflow'
  ));
}

interface CoTEditBatchCardProps {
  batch: EditBatch;
  getToken?: () => string | null;
  onBatchChange?: (batch: EditBatch) => void;
  onVersionReady?: (batch: EditBatch, htmlRevision?: HtmlEditApplyResult) => void;
  onRecoveryVersionReady?: (result: DocEditRecoveryResult) => void;
  documentVersions?: DocumentVersionSummary[];
  onSelectDocumentVersion?: (version: DocumentVersionSummary) => void;
  artifactFormat?: 'source' | 'pdf';
}

function DiffPreview({ segments }: { segments: DiffSegment[] }) {
  return (
    <div
      style={{
        borderRadius: 6,
        paddingBlock: 10,
        paddingInline: 11,
        backgroundColor: APP.bgDocSheet,
        border: `1px solid ${APP.border}`,
        fontFamily: '"SFMono-Regular", ui-monospace, Consolas, "Liberation Mono", monospace',
        fontSize: 11,
        lineHeight: '17px',
        color: APP.textDark,
        overflowWrap: 'anywhere',
      }}
    >
      {segments.map((segment, index) => (
        <span
          key={`${segment.kind}-${index}`}
          style={{
            color: segment.kind === 'insert' ? '#1B7A4B' : segment.kind === 'delete' ? '#B9382D' : APP.textDark,
            textDecoration: segment.kind === 'delete' ? 'line-through' : 'none',
            fontWeight: segment.kind === 'context' ? 400 : 600,
          }}
        >
          {segment.text}
        </span>
      ))}
    </div>
  );
}

function TextButton({
  children,
  onClick,
  disabled,
  primary,
  title,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        borderRadius: 6,
        paddingBlock: 7,
        paddingInline: 12,
        backgroundColor: primary ? APP.textDark : 'transparent',
        border: primary ? 'none' : `1px solid ${APP.border}`,
        color: primary ? '#FDFCFB' : APP.textCardDetail,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        fontFamily: F,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: '14px',
      }}
    >
      {children}
    </button>
  );
}

function SettledRow({ edit, applyMode }: { edit: Edit; applyMode?: EditBatch['applyMode'] }) {
  const accepted = edit.status === 'accepted';
  const acceptedLabel = applyMode === 'conversation_html_revision' ? 'Accepted' : 'Applied';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        borderRadius: 6,
        paddingBlock: 8,
        paddingInline: 10,
        backgroundColor: accepted ? APP.bgReviewCardAccepted : APP.bgReviewCardDismissed,
        border: `1px solid ${accepted ? APP.borderReviewCardAccepted : APP.borderReviewCardDismissed}`,
        color: accepted ? APP.textReviewAccepted : APP.textReviewDismissed,
        fontFamily: F,
        fontSize: 11,
        lineHeight: '15px',
      }}
    >
      {accepted ? <Check size={13} /> : <X size={13} />}
      <span style={{ minWidth: 0 }}>
        {accepted ? acceptedLabel : 'Declined'}: {edit.description}
      </span>
    </div>
  );
}

/**
 * Renders a single state-aware banner for the inline review card.
 * Each non-idle state gets a distinct visual treatment, message, and
 * action set. Critically:
 *   - `blocked_infra` shows a Retry button (infra error, edit content is fine).
 *   - `blocked_unsafe` does NOT show retry — the user has to revise edits.
 *   - `verification_unavailable` is a soft warning; original PDF preserved.
 */
function CardStateBanner({
  state,
  onRetry,
  isHtmlRevisionBatch,
  finalPolicyBlock,
  onCreateEditableDraftCopy,
  onEditUnsignedVersion,
  onOpenSourceDocument,
  canEditUnsignedVersion,
  canOpenSourceDocument,
  recoveryBusy,
}: {
  state: CardState;
  onRetry: () => void;
  isHtmlRevisionBatch: boolean;
  finalPolicyBlock: boolean;
  onCreateEditableDraftCopy?: () => void;
  onEditUnsignedVersion?: () => void;
  onOpenSourceDocument?: () => void;
  canEditUnsignedVersion?: boolean;
  canOpenSourceDocument?: boolean;
  recoveryBusy?: boolean;
}) {
  if ((state.kind === 'idle' && !finalPolicyBlock) || state.kind === 'applying') return null;
  if (isHtmlRevisionBatch && state.kind !== 'error') return null;

  const palette = (() => {
    if (finalPolicyBlock) return { bg: '#F4F1EC', border: APP.border, text: APP.textCardDetail };
    switch (state.kind) {
      case 'applied':
        return { bg: '#F2F8F4', border: APP.borderReviewCardAccepted, text: APP.textReviewAccepted };
      case 'partial':
        return { bg: '#FFF8E8', border: '#E8C97D', text: '#7A4A0F' };
      case 'blocked_infra':
        return { bg: '#FFF4F2', border: '#E8B7AE', text: '#9D2E22' };
      case 'blocked_unsafe':
      case 'verification_unavailable':
        return { bg: '#F4F1EC', border: APP.border, text: APP.textCardDetail };
      case 'stale':
        return { bg: '#F8F6F3', border: APP.border, text: APP.textCardDetail };
      case 'error':
      default:
        return { bg: '#FFF4F2', border: '#E8B7AE', text: '#9D2E22' };
    }
  })();

  const { title, body, icon } = (() => {
    if (finalPolicyBlock) {
      return {
        title: 'Signed document preserved',
        body: 'This document is finalized, so Corner preserves its exact layout. This change is longer than the original text and cannot be applied without altering the signed document.',
        icon: <ShieldAlert size={14} />,
      };
    }
    switch (state.kind) {
      case 'applied': {
        const applied =
          state.outcome?.kind === 'applied' ||
          state.outcome?.kind === 'applied_exact' ||
          state.outcome?.kind === 'applied_local_reflow'
            ? state.outcome
            : null;
        const count = applied?.appliedCount ?? 0;
        const isLocalReflow = state.outcome?.kind === 'applied_local_reflow';
        const isExact = state.outcome?.kind === 'applied_exact';
        return {
          title: isLocalReflow
            ? `Applied ${count} change${count === 1 ? '' : 's'} with local reflow`
            : isExact
              ? `Applied ${count} exact change${count === 1 ? '' : 's'}`
              : `Applied ${count} change${count === 1 ? '' : 's'}`,
          body: isLocalReflow
            ? 'A new version was saved by redrawing only the local text region around the edit.'
            : 'A new version of the document has been saved.',
          icon: <Check size={14} />,
        };
      }
      case 'partial': {
        const partial = state.outcome?.kind === 'partial' ? state.outcome : null;
        return {
          title: `Applied ${partial?.appliedCount ?? 0} change${(partial?.appliedCount ?? 0) === 1 ? '' : 's'}, ${partial?.blocked.length ?? 0} blocked`,
          body: 'Some edits could not preserve the original PDF layout. The blocked edits remain available below.',
          icon: <AlertTriangle size={14} />,
        };
      }
      case 'blocked_infra':
        return {
          title: 'Verification temporarily unavailable',
          body: state.detail || 'Corner could not load the original PDF to verify these edits. The document was not changed.',
          icon: <RefreshCw size={14} />,
        };
      case 'blocked_unsafe':
        if (state.outcome?.kind === 'blocked_final_document') {
          return {
            title: 'Signed document preserved',
            body: 'This document is finalized, so Corner preserves its exact layout. This change is longer than the original text and cannot be applied without altering the signed document.',
            icon: <ShieldAlert size={14} />,
          };
        }
        if (state.outcome?.kind === 'blocked_overflow') {
          return {
            title: 'This edit needs a wider local region',
            body: state.detail || 'Corner could not fit the replacement inside a safe local PDF region. The original PDF was preserved.',
            icon: <ShieldAlert size={14} />,
          };
        }
        return {
          title: 'These edits cannot preserve the original PDF layout',
          body: state.detail || 'Review the edits below — accepting them would change the document layout. The original PDF was preserved.',
          icon: <ShieldAlert size={14} />,
        };
      case 'verification_unavailable':
        return {
          title: 'Layout verification unavailable for this file',
          body: state.detail || 'Corner could not verify edits against this PDF (it may be encrypted or scanned). The original PDF was preserved.',
          icon: <ShieldAlert size={14} />,
        };
      case 'stale':
        return {
          title: 'These proposed edits expired',
          body: state.detail || 'This review card came from an older session, but the underlying edit batch is no longer available. Ask Corner to regenerate the change and review the fresh proposal.',
          icon: <AlertTriangle size={14} />,
        };
      case 'error':
      default:
        return {
          title: 'Could not apply changes',
          body: state.detail || 'Corner could not apply these changes. The original document was preserved.',
          icon: <AlertTriangle size={14} />,
        };
    }
  })();

  const showRetry = state.kind === 'blocked_infra'
    && state.outcome?.kind === 'blocked_infra'
    && state.outcome.canRetry;
  const showFinalRecovery = finalPolicyBlock && Boolean(onCreateEditableDraftCopy);

  return (
    <div
      role={state.kind === 'applied' ? 'status' : 'alert'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        borderRadius: 6,
        paddingBlock: 9,
        paddingInline: 11,
        backgroundColor: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.text,
        fontFamily: F,
        fontSize: 11,
        lineHeight: '15px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontWeight: 600 }}>
        {icon}
        <span>{title}</span>
      </div>
      <div>{body}</div>
      {showRetry && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
          <TextButton primary onClick={onRetry} title="Retry applying these edits">
            <RefreshCw size={12} />
            Retry
          </TextButton>
        </div>
      )}
      {showFinalRecovery && (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
          <TextButton
            primary
            disabled={recoveryBusy}
            onClick={onCreateEditableDraftCopy!}
            title="Create a DOCX draft copy without changing the signed PDF"
          >
            {recoveryBusy ? <RefreshCw size={12} /> : null}
            Create editable draft copy
          </TextButton>
          {canEditUnsignedVersion && onEditUnsignedVersion && (
            <TextButton
              disabled={recoveryBusy}
              onClick={onEditUnsignedVersion}
              title="Switch to the nearest unsigned version"
            >
              Edit unsigned version
            </TextButton>
          )}
          {canOpenSourceDocument && onOpenSourceDocument && (
            <TextButton
              disabled={recoveryBusy}
              onClick={onOpenSourceDocument}
              title="Open the structured source document"
            >
              Open source document
            </TextButton>
          )}
        </div>
      )}
    </div>
  );
}

const DOC_EDIT_MODAL_DEBUG = (() => {
  try {
    return Boolean((import.meta as unknown as { env?: { VITE_DOC_EDIT_DEBUG?: string } }).env?.VITE_DOC_EDIT_DEBUG === '1');
  } catch {
    return false;
  }
})();

function modalStateLog(state: CardState, batchId: string): void {
  if (!DOC_EDIT_MODAL_DEBUG) return;
  console.log('[DocEditModal] state', {
    state: state.kind,
    batchId,
    reason: state.outcome && state.outcome.kind !== 'applied' && 'detail' in state.outcome ? state.outcome.detail : null,
  });
}

export default function CoTEditBatchCard({
  batch,
  getToken,
  onBatchChange,
  onVersionReady,
  onRecoveryVersionReady,
  documentVersions = [],
  onSelectDocumentVersion,
  artifactFormat = 'source',
}: CoTEditBatchCardProps) {
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [lastOutcome, setLastOutcome] = useState<EditApplyOutcome | null>(null);
  const [staleMessage, setStaleMessage] = useState<string | null>(null);
  const lastNotifiedVersionFileIdRef = useRef<string | null>(null);
  const htmlApplyInFlightRef = useRef(false);
  const pendingCount = batch.edits.filter((edit) => edit.status === 'pending').length;
  const pdfIneligiblePendingCount = batch.edits.filter((edit) => (
    edit.status === 'pending' && pdfEligibilityForEdit(edit)?.overlayEligible === false
  )).length;
  const pdfEligiblePendingCount = overlayEligibleCount(batch);
  const hasPdfEligibilityLimits = artifactFormat === 'pdf' && pdfIneligiblePendingCount > 0;
  const acceptedCount = batch.edits.filter((edit) => edit.status === 'accepted').length;
  const isHtmlRevisionBatch =
    batch.applyMode === 'conversation_html_revision' ||
    Boolean(batch.conversationId && batch.baseDocumentId && batch.edits.some((edit) => edit.htmlAction));
  const resultVersion = batch.resultingVersion?.version;
  const baseVersion = batch.baseDocumentVersion;
  const htmlBatchApplied =
    isHtmlRevisionBatch &&
    Boolean(batch.resultingVersion.fileId) &&
    (baseVersion == null || String(resultVersion) !== String(baseVersion));

  // If the batch identity changes (e.g. a fresh plan was streamed in),
  // reset the outcome so we don't keep showing a stale banner.
  const lastBatchIdRef = useRef(batch.id);
  if (lastBatchIdRef.current !== batch.id) {
    lastBatchIdRef.current = batch.id;
    if (lastOutcome) setLastOutcome(null);
    if (errorMessage) setErrorMessage(null);
    if (staleMessage) setStaleMessage(null);
  }

  const cardState: CardState = staleMessage
    ? { kind: 'stale', outcome: null, detail: staleMessage }
    : lastOutcome
      ? stateFromOutcome(lastOutcome)
      : deriveStateFromBatch(batch, busyKey);
  const pdfApplyBlocked = !isHtmlRevisionBatch && artifactFormat === 'pdf' && isBlockedApplyState(cardState);
  const applyEnabled = busyKey === null && !staleMessage && (
    isHtmlRevisionBatch ||
    artifactFormat !== 'pdf' ||
    (pdfEligiblePendingCount > 0 && !pdfApplyBlocked)
  );
  const finalPolicyBlock = isFinalDocumentPolicyBlock(batch, cardState);
  const unsignedVersionTarget = finalPolicyBlock ? findUnsignedVersionTarget(batch, documentVersions) : null;
  const rawSourceDocumentTarget = finalPolicyBlock ? findSourceDocumentTarget(batch, documentVersions) : null;
  const sourceDocumentTarget = rawSourceDocumentTarget?.id === unsignedVersionTarget?.id ? null : rawSourceDocumentTarget;
  const recoveryBusy = busyKey === 'recovery-docx';

  // Log state transitions only when the kind changes — avoids spamming
  // the console on every render when waiting in `idle`.
  const lastLoggedStateRef = useRef<CardStateKind | null>(null);
  useEffect(() => {
    if (lastLoggedStateRef.current !== cardState.kind) {
      lastLoggedStateRef.current = cardState.kind;
      modalStateLog(cardState, batch.id);
    }
  }, [cardState.kind, batch.id, cardState.outcome]);

  const lastRecoveryLogRef = useRef<string | null>(null);
  useEffect(() => {
    if (!finalPolicyBlock) return;
    const outcome = cardState.outcome?.kind ?? cardState.kind;
    const key = `${batch.id}:${outcome}`;
    if (lastRecoveryLogRef.current === key) return;
    lastRecoveryLogRef.current = key;
    console.log('[DocEditRecovery] final-doc-blocked', {
      documentId: batch.docId,
      batchId: batch.id,
      policy: batch.editPolicy,
      outcome,
      suggestedRecovery: 'editable_draft_docx',
    });
  }, [batch.docId, batch.editPolicy, batch.id, cardState.kind, cardState.outcome?.kind, finalPolicyBlock]);

  const hydratedBatchIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (hydratedBatchIdRef.current === batch.id) return;
    const token = getToken?.();
    if (!token) return;
    hydratedBatchIdRef.current = batch.id;
    let cancelled = false;
    void fetch(`/api/orchestrate/edits/${batch.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        return res.json().catch(() => null) as Promise<{ editBatch?: EditBatch | null; stale?: boolean; code?: string; message?: string } | null>;
      })
      .then((data) => {
        if (cancelled || !data) return;
        if (data.stale || data.code === 'EDIT_BATCH_NOT_FOUND' || !data.editBatch) {
          const message = data.message || 'This edit batch is no longer available. Regenerate edits to review them again.';
          console.warn('[DocEditPhase2] hydrate-review-stale', {
            batchId: batch.id,
            code: data.code ?? null,
            message,
          });
          setStaleMessage(message);
          return;
        }
        if (data.editBatch.id !== batch.id) return;
        console.log('[DocEditPhase2] hydrate-review', {
          batchId: data.editBatch.id,
          conversationId: data.editBatch.conversationId ?? null,
          documentId: data.editBatch.docId,
          overlayEligibleCount: overlayEligibleCount(data.editBatch),
          blockedReasons: blockedReasonsForBatch(data.editBatch),
          cardState: deriveStateFromBatch(data.editBatch, null).kind,
          applyEnabled: !isHtmlRevisionBatch && artifactFormat === 'pdf'
            ? overlayEligibleCount(data.editBatch) > 0 && !isBlockedApplyState(deriveStateFromBatch(data.editBatch, null))
            : true,
        });
        if (data.editBatch.lastOutcome) setLastOutcome(data.editBatch.lastOutcome);
        setStaleMessage(null);
        onBatchChange?.(data.editBatch);
      })
      .catch(() => {
        // Network failures should not mark the batch stale; the user
        // may still be able to apply using the hydrated snapshot.
      });
    return () => {
      cancelled = true;
    };
  }, [batch.id, getToken, onBatchChange]);

  const applyBatch = (next: EditBatch, htmlRevision?: HtmlEditApplyResult) => {
    onBatchChange?.(next);
    if (!isHtmlRevisionBatch) {
      window.dispatchEvent(new CustomEvent('corner:working-doc-updated', { detail: { docId: next.docId } }));
    }
    const nextAcceptedCount = next.edits.filter((edit) => edit.status === 'accepted').length;
    const nextVersionFileId =
      next.resultingVersion.fileId ??
      (htmlRevision?.documentId ? `doc-${htmlRevision.documentId}` : null);
    console.log('[DocEditAcceptAll] onBatchChange', {
      batchId: next.id,
      docId: next.docId,
      resultingFileId: nextVersionFileId,
      resultingVersion: next.resultingVersion?.version ?? null,
      acceptedCount: nextAcceptedCount,
      pendingCount: next.edits.filter((edit) => edit.status === 'pending').length,
    });
    const shouldNotifyVersionReady = Boolean(nextVersionFileId && (htmlRevision || nextAcceptedCount > 0));
    if (shouldNotifyVersionReady && nextVersionFileId !== lastNotifiedVersionFileIdRef.current) {
      lastNotifiedVersionFileIdRef.current = nextVersionFileId;
      if (htmlRevision) {
        console.log('[HtmlEditApply] notify-version-ready', {
          batchId: next.id,
          documentId: htmlRevision.documentId,
          documentVersion: htmlRevision.documentVersion,
        });
      }
      onVersionReady?.(next, htmlRevision);
    }
  };

  const createEditableDraftCopy = async () => {
    const token = getToken?.();
    if (!token) {
      setErrorMessage('Please sign in again to create an editable draft copy.');
      return;
    }
    setBusyKey('recovery-docx');
    setErrorMessage(null);
    try {
      const res = await fetch(`/api/orchestrate/edits/${batch.id}/recovery/editable-draft-docx`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({})) as Partial<DocEditRecoveryResult> & { error?: string; code?: string };
      if (!res.ok || !data.editBatch || !data.fileId) {
        throw new Error(data.error || 'Corner could not create an editable draft copy.');
      }
      console.log('[DocEditRecovery] editable-draft-created', {
        batchId: batch.id,
        documentId: batch.docId,
        fileId: data.fileId,
        versionId: data.versionId ?? data.recovery?.selectedVersionId ?? null,
      });
      onBatchChange?.(data.editBatch);
      onRecoveryVersionReady?.(data as DocEditRecoveryResult);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Corner could not create an editable draft copy.');
    } finally {
      setBusyKey(null);
    }
  };

  const call = async (path: string, busy: string) => {
    const token = getToken?.();
    if (!token) {
      setErrorMessage('Please sign in again to review this change.');
      return;
    }
    if (staleMessage) {
      setErrorMessage(staleMessage);
      return;
    }
    const isAcceptAll = busy === 'accept-all';
    if (isAcceptAll) {
      console.log('[DocEditAcceptAll] click', {
        batchId: batch.id,
        docId: batch.docId,
        actionCount: batch.edits.length,
        actionTypes: batch.edits.map((edit) => edit.action?.type ?? null),
        eligibleActionCount: batch.edits.filter((edit) => edit.status === 'pending' && pdfEligibilityForEdit(edit)?.overlayEligible !== false).length,
        ineligibleActionCount: batch.edits.filter((edit) => edit.status === 'pending' && pdfEligibilityForEdit(edit)?.overlayEligible === false).length,
        artifactFormat,
      });
    }
    setBusyKey(busy);
    setErrorMessage(null);
    try {
      const url = artifactFormat === 'pdf'
        ? `${path}${path.includes('?') ? '&' : '?'}artifactFormat=pdf`
        : path;
      if (isAcceptAll) {
        console.log('[DocEditAcceptAll] request', {
          batchId: batch.id,
          docId: batch.docId,
          url,
          actionCount: batch.edits.length,
          actionTypes: batch.edits.map((edit) => edit.action?.type ?? null),
          eligibleActionCount: batch.edits.filter((edit) => edit.status === 'pending' && pdfEligibilityForEdit(edit)?.overlayEligible !== false).length,
          ineligibleActionCount: batch.edits.filter((edit) => edit.status === 'pending' && pdfEligibilityForEdit(edit)?.overlayEligible === false).length,
        });
      }
      const res = await fetch(url, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({})) as {
        editBatch?: EditBatch;
        error?: string;
        code?: string;
        outcome?: EditApplyOutcome;
      };
      if (isAcceptAll) {
        console.log('[DocEditAcceptAll] response', {
          batchId: batch.id,
          docId: batch.docId,
          status: res.status,
          hasEditBatch: Boolean(data.editBatch),
          resultingVersionFileId: data.editBatch?.resultingVersion?.fileId ?? null,
          code: data.code ?? null,
          outcomeKind: data.outcome?.kind ?? null,
          actionCount: data.editBatch?.edits.length ?? batch.edits.length,
          actionTypes: (data.editBatch?.edits ?? batch.edits).map((edit) => edit.action?.type ?? null),
        });
      }
      if (data.outcome) {
        setLastOutcome(data.outcome);
      }
      if (!res.ok) {
        // Trust the discriminated outcome when present — it already
        // tells us whether to show "retry" (infra) vs "manual review"
        // (unsafe) vs "verification unavailable". We still keep the
        // legacy code-based messages as a fallback for older servers.
        if (data.outcome) {
          // We've already set state via setLastOutcome; surface a
          // user-friendly inline error string only for non-PDF paths
          // (`error` outcomes), because the dedicated state banners
          // already explain the other cases.
          if (data.outcome.kind === 'error') {
            throw new Error(data.outcome.detail || data.error || 'Corner could not apply that change.');
          }
          // For pdf-specific blocked states, suppress the duplicate
          // error banner — the state-machine banner has the message.
          if (data.editBatch) applyBatch(data.editBatch);
          return;
        }
        if (data.code === 'PDF_LAYOUT_VERIFICATION_UNAVAILABLE') {
          throw new Error('Layout-preserving verification is temporarily unavailable for this file. The original PDF was preserved.');
        }
        if (data.code === 'UNSAFE_FOR_LAYOUT_PRESERVING_PDF_EDIT') {
          throw new Error('This edit is not supported in Preserve PDF layout mode. The original PDF was preserved.');
        }
        if (data.code === 'PDF_LAYOUT_PRESERVATION_FAILED') {
          throw new Error('This edit could not be applied without changing the document layout. The original PDF was preserved.');
        }
        throw new Error(data.error || 'Corner could not apply that change.');
      }
      if (!data.editBatch) {
        throw new Error('Corner did not return the updated review state.');
      }
      if (isAcceptAll && !isHtmlRevisionBatch && !data.editBatch.resultingVersion?.fileId) {
        console.warn('[DocEditAcceptAll] missing-file-id', {
          batchId: batch.id,
          docId: batch.docId,
          actionCount: data.editBatch.edits.length,
          actionTypes: data.editBatch.edits.map((edit) => edit.action?.type ?? null),
          resultingVersionFileId: data.editBatch.resultingVersion?.fileId ?? null,
        });
        throw new Error('Edits could not be applied to the document text. No new version was created.');
      }
      if (data.editBatch) applyBatch(data.editBatch);
    } catch (error) {
      if (isAcceptAll) {
        console.error('[DocEditAcceptAll] failed', {
          batchId: batch.id,
          docId: batch.docId,
          actionCount: batch.edits.length,
          actionTypes: batch.edits.map((edit) => edit.action?.type ?? null),
          message: error instanceof Error ? error.message : String(error),
        });
      }
      setErrorMessage(error instanceof Error ? error.message : 'Corner could not apply that change.');
    } finally {
      setBusyKey(null);
    }
  };

  const updateHtmlReviewStatus = (status: 'accepted' | 'rejected', editId?: string) => {
    console.log('[HtmlEditApply] review-change', {
      batchId: batch.id,
      applyMode: batch.applyMode,
      status,
      editId: editId ?? null,
      pendingCount,
      acceptedCount,
    });
    const next: EditBatch = {
      ...batch,
      edits: batch.edits.map((edit) => {
        if (editId && edit.id !== editId) return edit;
        if (edit.status !== 'pending') return edit;
        return { ...edit, status };
      }),
    };
    applyBatch(next);
  };

  const applyHtmlChanges = async (acceptedEditIds: string[], optimisticBatch?: EditBatch) => {
    console.log('[HtmlEditApply] click', {
      batchId: batch.id,
      applyMode: batch.applyMode,
      acceptedCount: acceptedEditIds.length,
      conversationId: batch.conversationId ?? null,
      documentId: batch.baseDocumentId ?? null,
      documentVersion: batch.baseDocumentVersion ?? null,
    });
    if (acceptedEditIds.length <= 0) {
      console.log('[HtmlEditApply] skipped-click', {
        reason: 'no-accepted-edits',
        batchId: batch.id,
        applyMode: batch.applyMode,
      });
      return;
    }
    if (htmlBatchApplied) {
      console.log('[HtmlEditApply] skipped-click', {
        reason: 'already-applied',
        batchId: batch.id,
        applyMode: batch.applyMode,
        documentVersion: batch.resultingVersion?.version ?? null,
      });
      return;
    }
    if (htmlApplyInFlightRef.current) {
      console.log('[HtmlEditApply] skipped-click', {
        reason: 'apply-in-flight',
        batchId: batch.id,
        applyMode: batch.applyMode,
      });
      return;
    }
    const token = getToken?.();
    if (!token) {
      console.log('[HtmlEditApply] skipped-click', {
        reason: 'missing-token',
        batchId: batch.id,
        applyMode: batch.applyMode,
      });
      setErrorMessage('Please sign in again to apply these changes.');
      return;
    }
    if (optimisticBatch) {
      applyBatch(optimisticBatch);
    }
    const requestBody = {
      batchId: batch.id,
      conversationId: batch.conversationId ?? null,
      documentId: batch.baseDocumentId ?? null,
      documentVersion: batch.baseDocumentVersion ?? null,
      acceptedEditIds,
    };
    console.log('[HtmlEditApply] request', requestBody);
    htmlApplyInFlightRef.current = true;
    setBusyKey('apply-html');
    setErrorMessage(null);
    try {
      const res = await fetch(`/api/document-edits/html/batches/${batch.id}/apply`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      const data = await res.json().catch(() => ({})) as Partial<HtmlEditApplyResult> & { error?: string };
      if (!res.ok) throw new Error(data.error || 'Corner could not apply those changes.');
      if (!data.editBatch || !data.documentId || data.documentVersion == null || !data.draftHtml) {
        throw new Error('Corner did not return the applied document version.');
      }
      console.log('[HtmlEditApply] applied', {
        batchId: batch.id,
        documentId: data.documentId,
        documentVersion: data.documentVersion,
        appliedEditCount: data.appliedIds?.length ?? acceptedEditIds.length,
        failedEditCount: data.failedIds?.length ?? 0,
      });
      applyBatch(data.editBatch, data as HtmlEditApplyResult);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Corner could not apply those changes.');
    } finally {
      htmlApplyInFlightRef.current = false;
      setBusyKey(null);
    }
  };

  const applyAcceptedHtmlChanges = () => {
    void applyHtmlChanges(batch.edits.filter((edit) => edit.status === 'accepted').map((edit) => edit.id));
  };

  const applyAllHtmlChanges = () => {
    const acceptedIds = batch.edits
      .filter((edit) => edit.status === 'accepted' || edit.status === 'pending')
      .map((edit) => edit.id);
    const optimisticBatch: EditBatch = {
      ...batch,
      edits: batch.edits.map((edit) => (
        edit.status === 'pending' ? { ...edit, status: 'accepted' } : edit
      )),
    };
    void applyHtmlChanges(acceptedIds, optimisticBatch);
  };

  const viewEdit = (edit: Edit) => {
    window.dispatchEvent(new CustomEvent('corner:view-edit-anchor', {
      detail: {
        docId: edit.location.docId,
        anchor: edit.location.anchor,
        page: edit.location.page,
        paragraphIndex: edit.location.paragraphIndex,
      },
    }));
  };

  const n = batch.edits.length;
  const batchActionsDisabled = Boolean(staleMessage);
  const acceptAllDisabled =
    batchActionsDisabled ||
    !applyEnabled ||
    (!isHtmlRevisionBatch && artifactFormat === 'pdf' && pdfEligiblePendingCount <= 0);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        borderRadius: 8,
        overflow: 'hidden',
        backgroundColor: APP.bgCardBody,
        border: `1px solid ${APP.borderReviewCardActive}`,
      }}
    >
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          width: '100%',
          padding: '10px 12px',
          border: 'none',
          borderBottom: collapsed ? 'none' : `1px solid ${APP.border}`,
          backgroundColor: APP.bgCardBody,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 600, color: APP.textDark, fontFamily: F }}>
          {collapsed ? <ChevronRight size={16} color={APP.textMuted} /> : <ChevronDown size={16} color={APP.textMuted} />}
          {n} tracked change{n === 1 ? '' : 's'}
        </span>
        {pendingCount > 0 && !collapsed && (
          <span style={{ fontSize: 11, color: APP.textMuted, fontFamily: F }}>
            {pendingCount} pending
          </span>
        )}
      </button>

      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12 }}>
          {pendingCount > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <TextButton
                primary
                disabled={acceptAllDisabled}
                onClick={() => (
                  isHtmlRevisionBatch
                    ? applyAllHtmlChanges()
                    : call(`/api/orchestrate/edits/${batch.id}/accept-all`, 'accept-all')
                )}
                title={acceptAllDisabled && artifactFormat === 'pdf'
                  ? 'No pending edits can preserve the original PDF layout'
                  : 'Accept all edits'}
              >
                {isHtmlRevisionBatch
                  ? 'Apply all changes'
                  : hasPdfEligibilityLimits && pdfEligiblePendingCount > 0
                    ? 'Accept layout-safe edits'
                    : 'Accept all'}
              </TextButton>
              {isHtmlRevisionBatch && (
                <TextButton
                  disabled={busyKey !== null || batchActionsDisabled}
                  onClick={() => updateHtmlReviewStatus('accepted')}
                  title="Accept all edits without saving yet"
                >
                  Accept all for review
                </TextButton>
              )}
              <TextButton
                disabled={busyKey !== null || batchActionsDisabled}
                onClick={() => (
                  isHtmlRevisionBatch
                    ? updateHtmlReviewStatus('rejected')
                    : call(`/api/orchestrate/edits/${batch.id}/reject-all`, 'reject-all')
                )}
                title="Decline all edits"
              >
                Decline all
              </TextButton>
              {hasPdfEligibilityLimits && (
                <span style={{ fontSize: 11, color: APP.textMuted, fontFamily: F, lineHeight: '15px' }}>
                  {pdfEligiblePendingCount} of {pendingCount} changes can preserve PDF layout.
                </span>
              )}
            </div>
          )}

          {isHtmlRevisionBatch && acceptedCount > 0 && !htmlBatchApplied && (
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <TextButton
                primary
                disabled={busyKey !== null || batchActionsDisabled}
                onClick={applyAcceptedHtmlChanges}
                title="Apply accepted changes to the document"
              >
                Apply accepted changes
              </TextButton>
              <span style={{ fontSize: 11, color: APP.textMuted, fontFamily: F, lineHeight: '15px' }}>
                Accepted changes are saved only after this step.
              </span>
            </div>
          )}

          {isHtmlRevisionBatch && acceptedCount <= 0 && pendingCount <= 0 && !htmlBatchApplied && (
            <div style={{ fontSize: 11, color: APP.textMuted, fontFamily: F, lineHeight: '15px' }}>
              No accepted edits to apply.
            </div>
          )}

          {htmlBatchApplied && (
            <div style={{ fontSize: 11, color: APP.textMuted, fontFamily: F, lineHeight: '15px' }}>
              Applied in v{batch.resultingVersion.version}.
            </div>
          )}

          {errorMessage && (
            <div
              role="alert"
              style={{
                borderRadius: 6,
                paddingBlock: 8,
                paddingInline: 10,
                backgroundColor: '#FFF4F2',
                border: '1px solid #E8B7AE',
                color: '#9D2E22',
                fontFamily: F,
                fontSize: 11,
                lineHeight: '15px',
              }}
            >
              {errorMessage}
            </div>
          )}

          <CardStateBanner
            state={cardState}
            onRetry={() => {
              setLastOutcome(null);
              call(`/api/orchestrate/edits/${batch.id}/accept-all`, 'accept-all');
            }}
            isHtmlRevisionBatch={isHtmlRevisionBatch}
            finalPolicyBlock={finalPolicyBlock}
            onCreateEditableDraftCopy={createEditableDraftCopy}
            onEditUnsignedVersion={unsignedVersionTarget && onSelectDocumentVersion
              ? () => onSelectDocumentVersion(unsignedVersionTarget)
              : undefined}
            onOpenSourceDocument={sourceDocumentTarget && onSelectDocumentVersion
              ? () => onSelectDocumentVersion(sourceDocumentTarget)
              : undefined}
            canEditUnsignedVersion={Boolean(unsignedVersionTarget && onSelectDocumentVersion)}
            canOpenSourceDocument={Boolean(sourceDocumentTarget && onSelectDocumentVersion)}
            recoveryBusy={recoveryBusy}
          />
          {batch.edits.map((edit) => {
            if (edit.status === 'accepted' || edit.status === 'rejected') {
              return <SettledRow key={edit.id} edit={edit} applyMode={batch.applyMode} />;
            }
            const superseded = edit.status === 'superseded';
            const pdfEligibility = pdfEligibilityForEdit(edit);
            const pdfUnsupported = artifactFormat === 'pdf' && pdfEligibility?.overlayEligible === false;
            const unsupportedReason = pdfEligibility?.reason ?? 'unverified_replacement';
            return (
              <div
                key={edit.id}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  borderRadius: 7,
                  padding: 12,
                  backgroundColor: APP.bgCodeBlock,
                  border: `1px solid ${APP.border}`,
                  opacity: superseded ? 0.78 : 1,
                }}
              >
                <span style={{ fontSize: 12, fontWeight: 500, color: APP.textDark, fontFamily: F, lineHeight: '17px' }}>
                  {edit.description}
                </span>
                <DiffPreview segments={edit.diff} />
                {superseded && (
                  <span style={{ fontSize: 11, color: APP.textMuted, fontFamily: F, lineHeight: '15px' }}>
                    This edit no longer matches the text at this location.
                  </span>
                )}
                {pdfUnsupported && (
                  <span style={{ fontSize: 11, color: APP.textMuted, fontFamily: F, lineHeight: '15px' }}>
                    Unsupported in Preserve PDF layout mode: {PDF_ELIGIBILITY_REASON_TEXT[unsupportedReason] ?? PDF_ELIGIBILITY_REASON_TEXT.unverified_replacement}
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <TextButton
                    primary
                    disabled={busyKey !== null || batchActionsDisabled || superseded || pdfUnsupported || pdfApplyBlocked}
                    onClick={() => (
                      isHtmlRevisionBatch
                        ? updateHtmlReviewStatus('accepted', edit.id)
                        : call(`/api/orchestrate/edits/${batch.id}/${edit.id}/accept`, edit.id)
                    )}
                    title={pdfUnsupported ? 'This edit cannot preserve the original PDF layout' : 'Accept edit'}
                  >
                    <Check size={12} />
                    Accept
                  </TextButton>
                  <TextButton
                    disabled={busyKey !== null || batchActionsDisabled || superseded}
                    onClick={() => (
                      isHtmlRevisionBatch
                        ? updateHtmlReviewStatus('rejected', edit.id)
                        : call(`/api/orchestrate/edits/${batch.id}/${edit.id}/reject`, edit.id)
                    )}
                    title="Decline edit"
                  >
                    <X size={12} />
                    Decline
                  </TextButton>
                  {superseded && !isHtmlRevisionBatch && (
                    <TextButton
                      disabled={busyKey !== null || batchActionsDisabled}
                      onClick={() => call(`/api/orchestrate/edits/${batch.id}/${edit.id}/reevaluate`, `reevaluate-${edit.id}`)}
                      title="Re-evaluate edit"
                    >
                      <RefreshCw size={12} />
                      Re-evaluate
                    </TextButton>
                  )}
                  <div style={{ flex: 1, minWidth: 8 }} />
                  <TextButton
                    disabled={busyKey !== null}
                    onClick={() => viewEdit(edit)}
                    title="View in document"
                  >
                    <Eye size={12} />
                    View
                  </TextButton>
                </div>
              </div>
            );
          })}

          {acceptedCount > 0 && batch.resultingVersion.downloadUrl && (
            <button
              type="button"
              disabled={!batch.resultingVersion.downloadUrl}
              onClick={() => {
                if (!batch.resultingVersion.downloadUrl) return;
                const name = batch.resultingVersion.filename ?? 'document';
                void downloadUrlWithAuthIfNeeded(batch.resultingVersion.downloadUrl, name, getToken ?? (() => null)).catch(() => {});
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                alignSelf: 'flex-start',
                borderRadius: 999,
                paddingBlock: 7,
                paddingInline: 12,
                backgroundColor: acceptedCount > 0 ? '#F2F8F4' : '#F8F6F3',
                border: `1px solid ${acceptedCount > 0 ? APP.borderReviewCardAccepted : APP.border}`,
                color: acceptedCount > 0 ? APP.textReviewAccepted : APP.textMuted,
                cursor: batch.resultingVersion.downloadUrl ? 'pointer' : 'default',
                fontFamily: F,
                fontSize: 11,
                fontWeight: 500,
                lineHeight: '14px',
              }}
            >
              <Download size={12} />
              {batch.resultingVersion.filename}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
